import React, { useEffect, useState } from 'react';
import { X, Loader2, ShieldCheck } from 'lucide-react';

/**
 * ExternalIframeModal — dumb iframe host for an external donation page
 * (e.g. a jGive campaign page) embedded on a public campaign page.
 *
 * Unlike PaymentIframeModal there is NO success/failure detection: the
 * external page never posts back to us. The donation is recorded by the
 * provider's webhook (settings → אינטגרציות); the donor simply closes the
 * modal when done.
 */

interface ExternalIframeModalProps {
  open: boolean;
  url: string;
  title: string;
  onClose: () => void;
}

export default function ExternalIframeModal({ open, url, title, onClose }: ExternalIframeModalProps) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 5000);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !url) return null;

  return (
    <div
      dir="rtl"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 md:p-6"
      style={{ background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)' }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden" style={{ height: 'min(92vh, 820px)' }}>
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-gray-200 bg-gradient-to-l from-blue-50 to-white shrink-0">
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-blue-600" />
            <h2 className="text-base md:text-lg font-bold text-gray-900">{title}</h2>
          </div>
          <button onClick={onClose} aria-label="סגור" className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-800">
            <X size={20} />
          </button>
        </div>
        <div className="relative flex-1 bg-gray-50">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="flex flex-col items-center gap-3 text-gray-500">
                <Loader2 size={36} className="animate-spin text-blue-500" />
                <span className="text-sm font-medium">טוען עמוד תרומה מאובטח...</span>
              </div>
            </div>
          )}
          <iframe
            src={url}
            title={title}
            onLoad={() => setLoading(false)}
            className="w-full h-full border-0"
            allow="payment *"
          />
        </div>
      </div>
    </div>
  );
}
